
import React from 'react';
import { ChatMessage } from './types';
import { formatDistanceToNow } from 'date-fns';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { MessageSquare, Book, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

interface MessageProps {
  message: ChatMessage;
}

export const Message: React.FC<MessageProps> = ({ message }) => {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';
  
  // Format the timestamp
  const formattedTime = formatDistanceToNow(new Date(message.timestamp), { addSuffix: true });

  // Process main content for the response text
  const processMainContent = () => {
    if (isUser || isSystem) return message.content;
    
    // For AI messages, try to extract just the main content before "Scripture References:" section
    const mainContentMatch = message.content.split(/Scripture References:|Recommended Tool:|For deeper guidance/i)[0];
    return mainContentMatch || message.content;
  };
  
  // Process tool references to make them clickable
  const renderToolRecommendations = () => {
    if (!message.recommendedTools || !Array.isArray(message.recommendedTools) || message.recommendedTools.length === 0) {
      return null;
    }
    
    return (
      <div className="mt-3 pt-3 border-t border-gray-200">
        <p className="text-xs font-semibold text-heaven-gold mb-2">RECOMMENDED TOOL</p>
        <div className="flex flex-col space-y-2">
          {message.recommendedTools.map((tool, index) => (
            <Link 
              key={index}
              to={tool.path} 
              className="flex items-center justify-between px-3 py-2 bg-heaven-gold/10 text-heaven-gold rounded-md hover:bg-heaven-gold/20 transition-colors"
            >
              <span>{tool.name}</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          ))}
        </div>
      </div>
    );
  };
  
  // Render scripture references section
  const renderScriptureReferences = () => {
    if (!message.scriptureReferences || !Array.isArray(message.scriptureReferences) || message.scriptureReferences.length === 0) {
      return null;
    }
    
    return (
      <div className="mt-3 pt-3 border-t border-gray-200">
        <p className="text-xs font-semibold text-heaven-gold mb-2">SCRIPTURE REFERENCES</p>
        <div className="space-y-2">
          {message.scriptureReferences.map((scripture, index) => (
            <div key={index} className="bg-[#E5DEFF] p-2 rounded-md">
              <p className="text-sm">
                <span className="font-semibold text-heaven-gold">{scripture.reference}:</span>{" "}
                <span className="italic">"{scripture.text}"</span>
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (isUser) {
    return (
      <div className="flex flex-row-reverse gap-3 mb-4">
        <div className="flex-shrink-0 mt-1">
          <Avatar className="h-8 w-8 bg-heaven-gold/20 border border-heaven-gold/30">
            <AvatarFallback className="text-heaven-gold">U</AvatarFallback>
          </Avatar>
        </div>
        <div className="max-w-[80%] items-end">
          <div className="px-4 py-3 rounded-xl text-sm bg-[#34495E] text-white">
            {message.content}
          </div>
          <div className="text-xs text-gray-500 mt-1 px-2">
            {formattedTime}
          </div>
        </div>
      </div>
    );
  }
  
  if (isSystem) {
    return (
      <div className="flex gap-3 mb-4">
        <div className="max-w-[80%] items-start">
          <div className="px-4 py-3 rounded-xl text-sm bg-gray-100 text-gray-700 italic border border-gray-200">
            {message.content}
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex gap-3 mb-4">
      <div className="flex-shrink-0 mt-1">
        <Avatar className="h-8 w-8 bg-[#34495E] border border-[#34495E]/30">
          <AvatarImage 
            src="https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//little%20dove%20(1).png" 
            alt="Selah Assistant" 
          />
          <AvatarFallback>
            <MessageSquare className="h-4 w-4 text-white" />
          </AvatarFallback>
        </Avatar>
      </div>
      
      <div className="max-w-[80%] items-start">
        <div className="px-4 py-3 rounded-xl text-sm bg-white border border-gray-200 text-gray-800">
          {processMainContent()}
          {renderScriptureReferences()}
          {renderToolRecommendations()}
        </div>
        <div className="text-xs text-gray-500 mt-1 px-2">
          {formattedTime}
        </div>
      </div>
    </div>
  );
};

